import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { UserPlus, X, Loader2, Plus, RefreshCw } from 'lucide-react';
import { usePartnerSearch, type PartnerSearchListing, type WaitlistPair } from '@/hooks/usePartnerSearch';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { toast } from 'sonner';
import PartnerPaymentModal from './PartnerPaymentModal';

interface PartnerSearchSectionProps {
  eventId: string;
  entryFee: number;
  category?: string | null;
  subcategory?: string | null;
  isUserRegistered?: boolean;
  onSearchCancelled?: () => void;
}

const PartnerSearchSection = ({
  eventId,
  entryFee,
  category,
  subcategory,
  isUserRegistered,
  onSearchCancelled,
}: PartnerSearchSectionProps) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const {
    loading,
    joining,
    userListing,
    isUserInDifferentCategory,
    inactiveUserListing,
    waitlists,
    joinSearch,
    cancelSearch,
  } = usePartnerSearch({ eventId, category, subcategory });

  const [cancellingSearch, setCancellingSearch] = useState(false);
  const [showNewListPicker, setShowNewListPicker] = useState(false);
  const [paymentModal, setPaymentModal] = useState<{
    open: boolean;
    clientSecret: string;
    listingId: string;
    paymentIntentId: string;
    position: 'drive' | 'reves';
  } | null>(null);

  const handleJoin = async (position: 'drive' | 'reves') => {
    if (!user) {
      toast.error(language === 'es' ? 'Debes iniciar sesión para buscar pareja' : 'You must sign in to find a partner');
      return;
    }
    if (isUserRegistered) {
      toast.info(language === 'es' ? 'Ya estás inscrito en este evento' : 'You are already registered for this event');
      return;
    }
    try {
      const result = await joinSearch(position);
      
      // If result has clientSecret, open payment modal
      if (result?.clientSecret) {
        setPaymentModal({
          open: true,
          clientSecret: result.clientSecret,
          listingId: result.listingId,
          paymentIntentId: result.paymentIntentId,
          position,
        });
        return;
      }
      
      // Free event — no payment needed
      const reactivated = result?.reactivated;
      toast.success(
        language === 'es'
          ? reactivated
            ? `Tu búsqueda fue reactivada como ${position === 'drive' ? 'Drive' : 'Revés'}. ¡Esperando pareja!`
            : `Te has unido como ${position === 'drive' ? 'Drive' : 'Revés'}. ¡Esperando pareja!`
          : reactivated
            ? `Your search was reactivated as ${position === 'drive' ? 'Drive (Forehand)' : 'Backhand'}. Waiting for a partner!`
            : `You joined as ${position === 'drive' ? 'Drive (Forehand)' : 'Backhand'}. Waiting for a partner!`
      );
      setShowNewListPicker(false);
    } catch {
      toast.error(language === 'es' ? 'Error al unirse a la búsqueda' : 'Error joining the search');
    }
  };

  const handleCancel = async () => {
    setCancellingSearch(true);
    try {
      await cancelSearch();
      toast.success(language === 'es' ? 'Has cancelado tu búsqueda' : 'You cancelled your search');
      onSearchCancelled?.();
    } catch {
      toast.error(language === 'es' ? 'Error al cancelar' : 'Error cancelling');
    } finally {
      setCancellingSearch(false);
    }
  };

  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const renderSlot = (
    listing: PartnerSearchListing | null,
    positionLabel: string,
    position: 'drive' | 'reves',
    canInteract: boolean
  ) => {
    const occupied = !!listing;
    const isCurrentUser = listing?.user_id === user?.id;
    const canJoin = !occupied && canInteract && !userListing && !joining && !!user;

    return (
      <div
        className={`flex-1 flex flex-col items-center gap-1 p-2 rounded-lg transition-all duration-200 ${
          canJoin ? 'cursor-pointer hover:bg-primary/5 active:scale-[0.97]' : ''
        }`}
        onClick={() => canJoin && handleJoin(position)}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">
          {positionLabel}
        </span>

        {occupied ? (
          <>
            <Avatar
              className="h-10 w-10 ring-2 ring-primary/20 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); navigate(`/profile/${listing.user_id}`, { state: { from: '/partner-search' } }); }}
            >
              <AvatarImage src={listing.profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-primary-foreground font-bold text-xs">
                {getInitials(listing.profile?.full_name)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium text-foreground truncate max-w-[90px]">
              {listing.profile?.full_name || (language === 'es' ? 'Jugador' : 'Player')}
            </span>
            {isCurrentUser && (
              <Badge variant="outline" className="text-[10px] px-2 py-0 border-primary/40 text-primary">
                {language === 'es' ? 'Tú' : 'You'}
              </Badge>
            )}
          </>
        ) : (
          <>
            <div className={`h-10 w-10 rounded-full border-2 border-dashed flex items-center justify-center ${
              canJoin ? 'border-primary/40' : 'border-muted-foreground/30'
            }`}>
              {joining ? (
                <Loader2 className="h-5 w-5 animate-spin text-primary/50" />
              ) : (
                <UserPlus className={`h-5 w-5 ${canJoin ? 'text-primary/50' : 'text-muted-foreground/40'}`} />
              )}
            </div>
            <span className={`text-xs ${canJoin ? 'text-primary' : 'text-muted-foreground'}`}>
              {language === 'es' ? 'Disponible' : 'Available'}
            </span>
          </>
        )}
      </div>
    );
  };

  const renderWaitlistCard = (pair: WaitlistPair, index: number) => {
    const isUserInThisPair = pair.drive?.user_id === user?.id || pair.reves?.user_id === user?.id;
    const canInteract = !userListing || isUserInThisPair;

    return (
      <div
        key={pair.id}
        className={`rounded-lg border p-2 transition-all duration-200 ${
          isUserInThisPair ? 'border-primary/30 bg-primary/5' : 'border-border'
        }`}
      >
        <p className="text-[10px] font-medium text-muted-foreground mb-1 text-center">
          {(() => {
            const cat = (pair.drive?.category || pair.reves?.category || category || '')?.trim();
            const sub = (pair.drive?.subcategory || pair.reves?.subcategory || subcategory || '')?.trim();
            return cat && sub ? `${cat} ${sub}` : cat || sub ? (cat || sub) : (language === 'es' ? `Lista ${index + 1}` : `List ${index + 1}`);
          })()}
        </p>
        <div className="flex gap-2">
          {renderSlot(pair.reves, language === 'es' ? 'Revés' : 'Backhand', 'reves', canInteract)}
          {renderSlot(pair.drive, 'Drive', 'drive', canInteract)}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Card className="bg-white">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (isUserRegistered) return null;

  return (
    <>
    <Card className="bg-white overflow-hidden">
      <CardContent className="space-y-3 pt-3">
        {/* Inactive listing notice */}
        {!userListing && inactiveUserListing && (
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-3 space-y-2">
            <p className="text-xs text-center text-destructive font-medium">
              {inactiveUserListing.status === 'expired'
                ? (language === 'es'
                  ? 'Tu búsqueda anterior expiró. Puedes volver a unirte seleccionando una posición.'
                  : 'Your previous search expired. You can rejoin by selecting a position.')
                : (language === 'es'
                  ? 'Tu búsqueda anterior fue cancelada. Puedes volver a unirte.'
                  : 'Your previous search was cancelled. You can rejoin.')}
            </p>
          </div>
        )}

        {/* Existing waitlists */}
        {waitlists.length > 0 ? (
          <div className="space-y-2">
            {waitlists.map((pair, i) => renderWaitlistCard(pair, i))}
          </div>
        ) : !showNewListPicker ? (
          <div className="rounded-lg border border-dashed border-primary/30 p-2">
            <div className="flex gap-2">
              {renderSlot(null, language === 'es' ? 'Revés' : 'Backhand', 'reves', true)}
              {renderSlot(null, 'Drive', 'drive', true)}
            </div>
          </div>
        ) : null}

        {/* New list picker */}
        {showNewListPicker && (
          <div className="rounded-lg border border-dashed border-primary/30 p-2">
            <p className="text-[10px] font-medium text-muted-foreground mb-1 text-center">
              {language === 'es' ? 'Nueva lista' : 'New list'}
            </p>
            <div className="flex gap-2">
              {renderSlot(null, language === 'es' ? 'Revés' : 'Backhand', 'reves', true)}
              {renderSlot(null, 'Drive', 'drive', true)}
            </div>
          </div>
        )}

        {/* Create new list button */}
        {!userListing && user && waitlists.length > 0 && !showNewListPicker && (
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() => setShowNewListPicker(true)}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            {language === 'es' ? 'Crear nueva lista' : 'Create new list'}
          </Button>
        )}

        {/* Entry fee */}
        {entryFee > 0 && (
          <div className="text-center">
            <span className="text-sm text-muted-foreground">
              {language === 'es' ? 'Costo por persona: ' : 'Cost per person: '}
            </span>
            <span className="text-sm font-bold text-foreground">${entryFee / 2}</span>
          </div>
        )}

        {/* User listing status + cancel */}
        {userListing && (
          <div className="space-y-2">
            {isUserInDifferentCategory ? (
              <p className="text-xs text-center text-destructive font-medium">
                {language === 'es'
                  ? `Ya tienes una búsqueda activa en ${userListing.subcategory || userListing.category || 'otra categoría'} como ${userListing.preferred_position === 'drive' ? 'Drive' : 'Revés'}. Cancela primero para unirte aquí.`
                  : `You already have an active search in ${userListing.subcategory || userListing.category || 'another category'} as ${userListing.preferred_position === 'drive' ? 'Drive' : 'Backhand'}. Cancel it first to join here.`}
              </p>
            ) : (
              <p className="text-xs text-center text-primary font-medium">
                {language === 'es'
                  ? `Estás buscando pareja como ${userListing.preferred_position === 'drive' ? 'Drive' : 'Revés'}`
                  : `You're searching as ${userListing.preferred_position === 'drive' ? 'Drive' : 'Backhand'}`}
              </p>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleCancel}
              disabled={cancellingSearch}
            >
              {cancellingSearch ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <X className="h-4 w-4 mr-2" />
              )}
              {language === 'es' ? 'Cancelar búsqueda' : 'Cancel search'}
            </Button>
          </div>
        )}

        {!user && (
          <p className="text-xs text-center text-muted-foreground">
            {language === 'es' ? 'Inicia sesión para buscar pareja' : 'Sign in to find a partner'}
          </p>
        )}
      </CardContent>
    </Card>

    {/* Stripe Payment Modal */}
    {paymentModal && (
      <PartnerPaymentModal
        open={paymentModal.open}
        clientSecret={paymentModal.clientSecret}
        listingId={paymentModal.listingId}
        paymentIntentId={paymentModal.paymentIntentId}
        entryFee={entryFee}
        position={paymentModal.position}
        onSuccess={({ matched, eventId }) => {
          setPaymentModal(null);
          if (matched && eventId) {
            toast.success(language === 'es' ? '¡Pareja encontrada! Redirigiendo al torneo...' : 'Partner found! Redirecting to tournament...');
            navigate(`/tournament/${eventId}`);
          } else {
            toast.success(
              language === 'es'
                ? `Te has unido como ${paymentModal.position === 'drive' ? 'Drive' : 'Revés'}. ¡Esperando pareja!`
                : `You joined as ${paymentModal.position === 'drive' ? 'Drive (Forehand)' : 'Backhand'}. Waiting for a partner!`
            );
            setShowNewListPicker(false);
          }
        }}
        onCancel={() => {
          setPaymentModal(null);
        }}
      />
    )}
    </>
  );
};

export default PartnerSearchSection;
